import type { FeatureFlagKeyType } from '@scrambl/shared'
import { PermissionActionEnum } from '@scrambl/shared'

/**
 * Settings Section Registry
 *
 * The single description of the sections inside each settings page, keyed by
 * the module nav href (matching useNavigationItems). Module settings pages
 * build their nav from it (ids, labels, order, default section, per-section
 * visibility flags), and OmniSearch, the settings hub, and the help-content
 * path audit read the same data to offer deep links like "Deals · Pipeline
 * Stages".
 *
 * This is intentionally DATA ONLY so it can live in the main bundle without
 * pulling lazy-loaded page chunks in with it. Icons and nav descriptions stay
 * in the page (see buildSettingsNavItems in components/shared/settings).
 * Section ids must match the SettingsNavItem ids the page passes to
 * SettingsLayout, and pages must resolve their active section from the URL
 * (via useSettingsSection, or their own ?section= handling) for the deep
 * link to land.
 *
 * When adding a section to a settings page, add it here and give the page's
 * presentation map an entry for it (the map is typed against this registry,
 * so the compiler flags a missing one). New modules also need a
 * SETTINGS_DIRECTORY entry in settings-directory.ts.
 */

export interface SettingsSectionDef {
  /** Section id as used in the page's SettingsLayout + URL param */
  readonly id: string
  readonly label: string
  readonly keywords?: readonly string[]
  /**
   * The section the page opens on when the URL names none. Absent on every
   * page today because each opens on its first section; set it when a page's
   * landing section is not the first in nav order.
   */
  readonly default?: boolean
  /**
   * Personal preference inside an org page: editable by its owner, so it is
   * never marked read-only and stays navigable when the rest of the group is.
   */
  readonly personal?: boolean
  /**
   * Gated on org admin + business account rather than the module MANAGE
   * permission (e.g. the client portal sections under entities).
   */
  readonly orgAdmin?: boolean
  /** Hidden for personal accounts (business chrome on a shared surface). */
  readonly businessOnly?: boolean
  /** Shown to personal accounts only (e.g. the account-type section). */
  readonly personalOnly?: boolean
  readonly featureFlag?: FeatureFlagKeyType
  /**
   * Extra permission action on the page's module that the section requires
   * in addition to the group's edit gate; the page and the hub filter with
   * the same rule. For example projects' Notifications rides PROJECT.APPROVE.
   */
  readonly gate?: PermissionActionEnum
}

export interface SettingsPageSections {
  /** URL query param carrying the section id (default 'section') */
  readonly param?: string
  readonly sections: readonly SettingsSectionDef[]
}

/** Keyed by module nav href from useNavigationItems */
const MODULE_SETTINGS_REGISTRY = {
  '/tasks/mine': {
    sections: [
      {
        id: 'statuses',
        label: 'Task Statuses',
        keywords: ['workflow', 'states'],
      },
      {
        id: 'templates',
        label: 'Task Templates',
        keywords: ['reusable', 'subtasks', 'checklist', 'onboarding'],
      },
      {
        id: 'recycle-bin',
        label: 'Recycle Bin',
        keywords: ['deleted', 'trash', 'restore'],
      },
    ],
  },
  '/tickets': {
    sections: [
      {
        id: 'ticket-types',
        label: 'Types & Stages',
        keywords: ['workflow', 'stages'],
      },
      { id: 'sources', label: 'Sources' },
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'email-intake', label: 'Inboxes', keywords: ['email', 'intake'] },
      { id: 'domains', label: 'Domains', keywords: ['email', 'dns'] },
      { id: 'widget', label: 'Customer Widget', keywords: ['embed', 'chat'] },
      { id: 'alerts', label: 'Alerts', keywords: ['sla', 'escalation'] },
      {
        id: 'macros',
        label: 'Macros',
        keywords: ['canned responses', 'templates'],
      },
      {
        id: 'suppressions',
        label: 'Suppressions',
        keywords: ['blocklist', 'spam'],
      },
      { id: 'defaults', label: 'Defaults' },
      {
        id: 'recycle-bin',
        label: 'Recycle Bin',
        keywords: ['deleted', 'trash'],
      },
    ],
  },
  '/projects': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'statuses', label: 'Statuses', keywords: ['workflow'] },
      { id: 'task-types', label: 'Task Types' },
      {
        id: 'cycles',
        label: 'Cycle Templates',
        keywords: ['sprints', 'iterations'],
      },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        keywords: ['access'],
      },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences', personal: true },
      // "Projects - Watch" rides PROJECT + APPROVE; hidden on personal accounts
      {
        id: 'notifications',
        label: 'Notifications',
        personal: true,
        businessOnly: true,
        gate: PermissionActionEnum.APPROVE,
      },
    ],
  },
  '/deals': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      {
        id: 'pipeline',
        label: 'Pipeline Stages',
        keywords: ['funnel', 'stages'],
      },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        keywords: ['access'],
      },
      { id: 'scoring', label: 'Scoring', keywords: ['lead scoring'] },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences', personal: true },
    ],
  },
  '/contacts': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      {
        id: 'relationship-types',
        label: 'Lifecycle',
        keywords: ['relationship types', 'stages'],
      },
      { id: 'scoring', label: 'Scoring', keywords: ['lead scoring'] },
      { id: 'categories', label: 'Categories' },
      { id: 'defaults', label: 'Defaults' },
    ],
  },
  '/entities': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      {
        id: 'lifecycle',
        label: 'Lifecycle Stages',
        keywords: ['stages', 'health'],
      },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        keywords: ['access'],
      },
      { id: 'scoring', label: 'Scoring' },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences', personal: true },
      // Client portal configuration is org-admin + business-account gated,
      // matching the ADMIN role the portal template endpoints enforce.
      {
        id: 'portal-branding',
        label: 'Branding',
        keywords: ['client portal'],
        orgAdmin: true,
      },
      {
        id: 'portal-permission-templates',
        label: 'Permission Templates',
        keywords: ['client portal', 'access'],
        orgAdmin: true,
      },
    ],
  },
  '/contracts': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'types', label: 'Contract Types' },
      { id: 'legal-statuses', label: 'Legal Statuses' },
      { id: 'reminders', label: 'Reminders', keywords: ['renewal', 'expiry'] },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences', personal: true },
    ],
  },
  '/invoices': {
    sections: [
      {
        id: 'defaults',
        label: 'Invoice Defaults',
        keywords: ['numbering', 'terms'],
      },
      { id: 'content', label: 'Content', keywords: ['branding', 'logo'] },
      { id: 'send', label: 'Send & Reminders', keywords: ['email', 'overdue'] },
      {
        id: 'payments',
        label: 'Payments',
        keywords: ['stripe', 'payment methods'],
      },
    ],
  },
  '/team': {
    sections: [
      { id: 'team-defaults', label: 'Team Defaults' },
      {
        id: 'default-rates',
        label: 'Default Rates',
        keywords: ['billing', 'cost', 'hourly'],
      },
      { id: 'billing-configuration', label: 'Billing Configuration' },
      {
        id: 'allocation',
        label: 'Allocation Policies',
        keywords: ['capacity'],
      },
      {
        id: 'holidays',
        label: 'Holidays',
        keywords: ['pto', 'vacation', 'time off'],
      },
      {
        id: 'compensation',
        label: 'Compensation',
        keywords: ['salary', 'pay'],
      },
      { id: 'utilization-definitions', label: 'Utilization Definitions' },
      { id: 'skills', label: 'Skills Settings' },
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        keywords: ['access'],
      },
    ],
  },
  '/financials': {
    sections: [
      {
        id: 'budget-categories',
        label: 'Budget Categories',
        keywords: ['budgets'],
      },
      {
        id: 'budget-templates',
        label: 'Budget Templates',
        keywords: ['budgets'],
      },
      {
        id: 'overhead',
        label: 'Overhead',
        keywords: ['overhead costs', 'allocation'],
      },
    ],
  },
  '/timesheets': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      {
        id: 'time-format',
        label: 'Time Format',
        keywords: ['duration', 'decimal'],
      },
      { id: 'pay-period', label: 'Pay Period', keywords: ['payroll'] },
      { id: 'expected-hours', label: 'Expected Hours', keywords: ['capacity'] },
      {
        id: 'entry-rules',
        label: 'Entry Rules',
        keywords: ['rounding', 'limits'],
      },
      { id: 'bill-to', label: 'Bill To' },
      {
        id: 'approvals',
        label: 'Approval Workflow',
        keywords: ['workflow', 'submit'],
      },
      { id: 'contractor-invoices', label: 'Contractor Invoices' },
      { id: 'notifications', label: 'Notifications' },
      {
        id: 'maintenance',
        label: 'Maintenance',
        keywords: ['rebuild', 'utilization'],
      },
    ],
  },
  '/products': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'category-types', label: 'Category Types' },
      {
        id: 'movement-reasons',
        label: 'Movement Reasons',
        keywords: ['stock'],
      },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        keywords: ['access'],
      },
    ],
  },
  '/events': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'event-types', label: 'Event Types' },
      { id: 'session-types', label: 'Session Types' },
      { id: 'agenda-types', label: 'Agenda Types' },
      { id: 'speaker-roles', label: 'Speaker Roles' },
      { id: 'host-roles', label: 'Host Roles' },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        keywords: ['access'],
      },
      { id: 'reminders', label: 'Reminders' },
      { id: 'defaults', label: 'Defaults' },
    ],
  },
  '/suppliers': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'lifecycle', label: 'Lifecycle Stages' },
      {
        id: 'payment-terms',
        label: 'Payment Terms',
        keywords: ['net terms', 'due date'],
      },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        keywords: ['access'],
      },
    ],
  },
  '/expenses': {
    sections: [
      { id: 'defaults', label: 'Defaults' },
      { id: 'categories', label: 'Categories' },
      { id: 'statuses', label: 'Statuses' },
      {
        id: 'payment-methods',
        label: 'Payment Methods',
        keywords: ['cards', 'reimbursement'],
      },
    ],
  },
} as const satisfies Record<string, SettingsPageSections>

/** Nav hrefs with a module settings page described in this registry. */
export type ModuleSettingsKey = keyof typeof MODULE_SETTINGS_REGISTRY

/** Union of the section ids registered for one module page. */
export type ModuleSettingsSectionId<K extends ModuleSettingsKey> =
  (typeof MODULE_SETTINGS_REGISTRY)[K]['sections'][number]['id']

/** Keyed by module nav href from useNavigationItems */
export const MODULE_SETTINGS_SECTIONS: Record<string, SettingsPageSections> =
  MODULE_SETTINGS_REGISTRY

/** Registered sections for one module page, in nav order. */
export function getModuleSettingsSections<K extends ModuleSettingsKey>(
  navHref: K,
): (typeof MODULE_SETTINGS_REGISTRY)[K]['sections'] {
  return MODULE_SETTINGS_REGISTRY[navHref].sections
}

/**
 * Every section id a module page renders, in nav order. Pages hand this to
 * useSettingsSection so an unknown `?section=` falls back to the default.
 */
export function getSettingsSectionIds(
  navHref: ModuleSettingsKey,
): readonly string[] {
  return MODULE_SETTINGS_REGISTRY[navHref].sections.map(section => section.id)
}

/** The section flagged `default`, or the first registered section. */
export function getDefaultSettingsSection(navHref: ModuleSettingsKey): string {
  const sections: readonly SettingsSectionDef[] =
    MODULE_SETTINGS_REGISTRY[navHref].sections
  return (sections.find(section => section.default) ?? sections[0]).id
}

/** Sections of the admin Organization Settings page (?section=) */
const ADMIN_SETTINGS_REGISTRY = [
  {
    id: 'general',
    label: 'General',
    keywords: ['identity', 'timezone', 'regional', 'locale', 'logo'],
  },
  {
    id: 'workspace-defaults',
    label: 'Workspace Defaults',
    keywords: ['task defaults', 'project defaults'],
  },
  {
    id: 'finance',
    label: 'Finance',
    keywords: ['currency', 'tax'],
    businessOnly: true,
  },
  {
    id: 'ai',
    label: 'Your AI',
    keywords: [
      'benny',
      'anthropic',
      'openai',
      'model',
      'provider',
      'api key',
      'bring your own',
    ],
    businessOnly: true,
    orgAdmin: true,
    featureFlag: 'BYO_AI',
  },
  {
    id: 'account',
    label: 'Account',
    keywords: ['account type', 'convert'],
    personalOnly: true,
  },
] as const satisfies readonly SettingsSectionDef[]

export type AdminSettingsSectionId =
  (typeof ADMIN_SETTINGS_REGISTRY)[number]['id']

/** Sections of the admin Organization Settings page (?section=) */
export const ADMIN_SETTINGS_SECTIONS: readonly SettingsSectionDef[] =
  ADMIN_SETTINGS_REGISTRY

/** Typed view of the admin sections for the page's presentation map. */
export function getAdminSettingsSections(): typeof ADMIN_SETTINGS_REGISTRY {
  return ADMIN_SETTINGS_REGISTRY
}

/** Hash tabs of the Account settings (user-settings) page */
export const USER_SETTINGS_TABS: SettingsSectionDef[] = [
  {
    id: 'account',
    label: 'Account',
    keywords: [
      'account',
      'avatar',
      'name',
      'display name',
      'photo',
      'email',
      'timezone',
      'delete account',
    ],
  },
  {
    id: 'appearance',
    label: 'Appearance',
    keywords: [
      'theme',
      'dark mode',
      'light mode',
      'system',
      'sidebar color',
      'glass',
      'appearance',
    ],
  },
  { id: 'skills', label: 'Skills' },
  {
    id: 'notifications',
    label: 'Notifications',
    keywords: ['alerts', 'email', 'push'],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    keywords: [
      'slack',
      'github',
      'outlook',
      'gmail',
      'connect',
      'mcp',
      'ai assistants',
      'agents',
      'api keys',
      'browser extension',
      'scrambl capture',
      'google calendar',
      'zoom',
    ],
  },
]
